import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { SpinnerComponent } from '../spinner/spinner.component';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { GlobalSearchResult, GlobalSearchService } from '../../services/global-search.service';

@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatIconModule,
    SpinnerComponent,
  ],
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  searchControl = new FormControl('');
  results: GlobalSearchResult[] = [];
  isLoading = false;
  panelOpen = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private globalSearchService: GlobalSearchService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter(term => typeof term === 'string' && term.length >= 2),
      tap(() => { this.isLoading = true; }),
      switchMap(term =>
        this.globalSearchService.search(term as string).pipe(
          catchError(() => of([] as GlobalSearchResult[]))
        )
      ),
      takeUntil(this.destroy$)
    ).subscribe(results => {
      this.results = results;
      this.isLoading = false;
    });

    this.searchControl.valueChanges.pipe(
      filter(term => typeof term !== 'string' || (term as string).length < 2),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.results = [];
      this.isLoading = false;
    });
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const result = event.option.value as GlobalSearchResult;
    if (result?.trackedBusinessApplicationId) {
      this.router.navigate(['/assessment', result.trackedBusinessApplicationId]);
    }
    this.searchControl.setValue('', { emitEvent: false });
    this.results = [];
  }

  displayFn(result: GlobalSearchResult | string | null): string {
    if (!result || typeof result === 'string') return result as string ?? '';
    return result.name ?? result.appId ?? '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
